import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import CasinoSidebar from '@/components/CasinoSidebar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DollarSign, ChevronRight, Check, Mail, Share2, TrendingUp, ArrowRight } from 'lucide-react';

function CountUp({ value, duration = 800 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    let start = 0;
    const startTime = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * value * 100) / 100);
      if (progress < 1) ref.current = requestAnimationFrame(animate);
    };
    ref.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(ref.current);
  }, [value, duration]);
  return <>{display.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>;
}

export default function GamblingBudgetCalculator() {
  const [income, setIncome] = useState('');
  const [expenses, setExpenses] = useState('');
  const [savings, setSavings] = useState('');
  const [risk, setRisk] = useState('moderate');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCalculate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/tools/calculate-budget', {
        monthly_income: parseFloat(income), fixed_expenses: parseFloat(expenses),
        savings_goal: parseFloat(savings), risk_tolerance: risk,
      });
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Calculation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailResult = () => { toast.info('Email feature coming soon! Results saved to your account.'); };
  const handleShare = () => { toast.info('Share link copied! (Feature coming soon)'); };

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="budget-calculator-page">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-6" data-testid="breadcrumb">
        <Link to="/dashboard" className="hover:text-[#4d7cfe] transition-colors">Dashboard</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-[#a0a8b8]">Play Smart</span>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-white">Gambling Budget Calculator</span>
      </nav>

      {/* Tool Header */}
      <div className="flex items-start gap-4 mb-8 animate-fade-in">
        <div className="w-12 h-12 rounded-xl bg-[#4d7cfe]/15 flex items-center justify-center shrink-0">
          <DollarSign className="h-6 w-6 text-[#4d7cfe]" />
        </div>
        <div>
          <h1 className="text-2xl lg:text-[32px] font-bold text-white" style={{ letterSpacing: '-0.005em' }}>Gambling Budget Calculator</h1>
          <p className="text-[#a0a8b8] text-sm mt-1">Calculate your safe monthly gambling budget based on real income analysis</p>
          <div className="flex items-center gap-1.5 mt-2 text-xs text-[#64748b]"><TrendingUp className="h-3 w-3" /> 12,450 uses this month</div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Input Form */}
            <div className="lg:w-[55%]">
              <div className="ciq-card">
                <form onSubmit={handleCalculate} className="space-y-5">
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Monthly Income (after tax)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">$</span>
                      <input type="number" value={income} onChange={(e) => setIncome(e.target.value)} required min="0" step="0.01"
                        className="w-full h-11 pl-8 pr-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none"
                        placeholder="3,000" data-testid="budget-income-input" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Monthly Fixed Expenses (rent, bills, groceries, etc.)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">$</span>
                      <input type="number" value={expenses} onChange={(e) => setExpenses(e.target.value)} required min="0" step="0.01"
                        className="w-full h-11 pl-8 pr-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none"
                        placeholder="2,000" data-testid="budget-expenses-input" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Monthly Savings Goal</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">$</span>
                      <input type="number" value={savings} onChange={(e) => setSavings(e.target.value)} required min="0" step="0.01"
                        className="w-full h-11 pl-8 pr-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none"
                        placeholder="300" data-testid="budget-savings-input" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Risk Tolerance</label>
                    <Select value={risk} onValueChange={setRisk}>
                      <SelectTrigger className="h-11 bg-[#1a1f2e] border-white/10 text-white" data-testid="budget-risk-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#252b3b] border-white/10">
                        <SelectItem value="conservative">Conservative (5%)</SelectItem>
                        <SelectItem value="moderate">Moderate (10%)</SelectItem>
                        <SelectItem value="aggressive">Aggressive (15%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <button type="submit" disabled={loading}
                    className="w-full h-12 rounded-xl bg-[#4d7cfe] text-white font-medium text-sm btn-lift disabled:opacity-50"
                    data-testid="budget-calculate-btn">
                    {loading ? 'Calculating...' : 'Calculate Safe Budget'}
                  </button>
                </form>
              </div>
            </div>

            {/* Results */}
            <div className="lg:w-[45%]">
              {result && (
                <div className="animate-slide-up space-y-4">
                  {/* Main Result */}
                  <div className="ciq-card border-[#10b981]/30 bg-[#10b981]/5">
                    <p className="text-sm text-[#a0a8b8] mb-1">Your Safe Monthly Gambling Budget</p>
                    <div className="text-3xl font-bold text-[#10b981]" data-testid="budget-result-amount">
                      $<CountUp value={result.safe_budget} />
                    </div>
                    <p className="text-xs text-[#64748b] mt-1">({result.risk_percentage} of your disposable income)</p>
                  </div>

                  {/* Breakdown */}
                  <div className="ciq-card">
                    <h3 className="text-white font-semibold text-sm mb-3">Breakdown</h3>
                    <div className="space-y-2.5">
                      {[
                        { label: 'Monthly Income', value: result.breakdown.monthly_income, prefix: '+', color: 'text-white' },
                        { label: 'Fixed Expenses', value: result.breakdown.fixed_expenses, prefix: '-', color: 'text-[#ef4444]' },
                        { label: 'Savings Goal', value: result.breakdown.savings_goal, prefix: '-', color: 'text-[#ef4444]' },
                      ].map((row, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-[#a0a8b8]">{row.label}</span>
                          <span className={row.color}>{row.prefix}${row.value.toLocaleString()}</span>
                        </div>
                      ))}
                      <div className="border-t border-white/10 pt-2 flex justify-between text-sm">
                        <span className="text-[#a0a8b8]">Disposable Income</span>
                        <span className="text-white font-medium">${result.breakdown.disposable_income.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm bg-[#10b981]/10 px-3 py-2 rounded-lg -mx-1">
                        <span className="text-[#10b981] font-medium">Recommended Budget</span>
                        <span className="text-[#10b981] font-bold" data-testid="budget-recommended">${result.breakdown.safe_budget.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Recommendations */}
                  <div className="ciq-card">
                    <h3 className="text-white font-semibold text-sm mb-3">Smart Playing Recommendations</h3>
                    <ul className="space-y-2.5">
                      {result.recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[#a0a8b8]">
                          <Check className="h-4 w-4 text-[#10b981] shrink-0 mt-0.5" /> {rec}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button onClick={handleEmailResult} className="flex-1 h-10 rounded-lg border border-white/10 text-[#a0a8b8] text-sm hover:bg-white/5 transition-colors flex items-center justify-center gap-2" data-testid="budget-email-btn">
                      <Mail className="h-3.5 w-3.5" /> Email Results
                    </button>
                    <button onClick={handleShare} className="flex-1 h-10 rounded-lg border border-white/10 text-[#a0a8b8] text-sm hover:bg-white/5 transition-colors flex items-center justify-center gap-2" data-testid="budget-share-btn">
                      <Share2 className="h-3.5 w-3.5" /> Share
                    </button>
                  </div>
                </div>
              )}

              {!result && (
                <div className="ciq-card flex flex-col items-center justify-center py-12 text-center">
                  <DollarSign className="h-10 w-10 text-[#3a4155] mb-3" />
                  <p className="text-[#64748b] text-sm">Fill in your details and click calculate to see your safe gambling budget</p>
                </div>
              )}
            </div>
          </div>

          {/* Casino Ad */}
          {result && (
            <div className="ciq-card mt-6 animate-fade-in" style={{ animationDelay: '300ms' }}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center shrink-0 p-1">
                  <img src="https://customer-assets.emergentagent.com/job_smart-player-hub/artifacts/cnni6myb_logo-light.png" alt="BitStarz" className="h-full object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium mb-1">Your ${result.safe_budget} budget goes further at BitStarz</p>
                  <p className="text-xs text-[#a0a8b8] mb-3">With 96.8% average RTP and games like Blood Suckers (98% RTP), your bankroll lasts longer. Fast crypto withdrawals when you win.</p>
                  <a href={`https://bzstarz1.com/bhi8kvogt?utm_source=casinoiq&utm_medium=budget-calculator&utm_campaign=smart-player-tools`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#4d7cfe] text-white text-sm font-medium btn-lift"
                    data-testid="budget-bitstarz-cta"
                    onClick={async () => { try { await api.post('/affiliate/click', { casino_id: 'bitstarz', source_page: 'budget-calculator' }); } catch {} }}>
                    Play at BitStarz <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        <CasinoSidebar sourcePage="gambling-budget-calculator" />
      </div>
    </div>
  );
}
